import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  BookOpen,
  Briefcase,
  Award,
  Plus,
  Trash2,
  Save,
  Settings,
  Palette,
  BookMarked,
  Star,
  Edit,
} from 'lucide-react';
import { ImageUpload } from '../components/ImageUpload';
import { VisibilitySelector, VisibilityBadge } from '../components/VisibilitySelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('settings');
  
  // Data states
  const [posts, setPosts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [books, setBooks] = useState([]);
  
  // Settings state
  const [siteSettings, setSiteSettings] = useState({
    name: '',
    title: '',
    tagline: '',
    bio: '',
    profile_image: '',
    github_url: '',
    linkedin_url: '',
    twitter_url: '',
    youtube_url: '',
    email: '',
    primary_color: '160 84% 39%',
    stats: [],
    skills: []
  });
  const [statsInput, setStatsInput] = useState('');
  const [skillsInput, setSkillsInput] = useState('');
  
  // Form states
  const [postForm, setPostForm] = useState({ 
    title: '', excerpt: '', content: '', tags: '', read_time: '5 min', 
    cover_image: '', visibility: 'public' 
  });
  const [journalForm, setJournalForm] = useState({ 
    content: '', tags: '', image_url: '', visibility: 'public' 
  });
  const [projectForm, setProjectForm] = useState({ 
    title: '', description: '', tech_stack: '', github_url: '', demo_url: '', 
    image_url: '', is_featured: false, visibility: 'public' 
  });
  const [certForm, setCertForm] = useState({ 
    title: '', issuer: '', date: '', credential_url: '', 
    image_url: '', visibility: 'public' 
  });
  const [bookForm, setBookForm] = useState({ 
    title: '', author: '', status: 'want_to_read', rating: null, 
    notes: '', cover_url: '', visibility: 'public' 
  });
  
  // Dialog states
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);

  // Edit mode states
  const [editingPost, setEditingPost] = useState(null);
  const [editingJournal, setEditingJournal] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingCert, setEditingCert] = useState(null);
  const [editingBook, setEditingBook] = useState(null);

  const fetchData = async () => {
    try {
      const [settingsRes, postsRes, journalsRes, projectsRes, certsRes, booksRes] = await Promise.all([
        fetch(`${API}/settings`),
        fetch(`${API}/admin/posts`, { credentials: 'include' }),
        fetch(`${API}/admin/journals`, { credentials: 'include' }),
        fetch(`${API}/admin/projects`, { credentials: 'include' }),
        fetch(`${API}/admin/certificates`, { credentials: 'include' }),
        fetch(`${API}/admin/books`, { credentials: 'include' })
      ]);
      
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setSiteSettings(settings);
        setStatsInput(settings.stats?.map(s => `${s.value}:${s.label}`).join(', ') || '');
        setSkillsInput(settings.skills?.join(', ') || '');
      }
      if (postsRes.ok) setPosts(await postsRes.json());
      if (journalsRes.ok) setJournals(await journalsRes.json());
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (certsRes.ok) setCertificates(await certsRes.json());
      if (booksRes.ok) setBooks(await booksRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // SETTINGS SAVE
  const handleSaveSettings = async () => {
    try {
      const stats = statsInput.split(',').map(s => {
        const [value, label] = s.trim().split(':');
        return value && label ? { value: value.trim(), label: label.trim() } : null;
      }).filter(Boolean);
      
      const skills = skillsInput.split(',').map(s => s.trim()).filter(Boolean);
      
      const settingsToSave = { ...siteSettings, stats, skills };
      
      const response = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settingsToSave)
      });
      
      if (response.ok) {
        toast.success('Settings saved!');
        if (siteSettings.primary_color) {
          document.documentElement.style.setProperty('--primary', siteSettings.primary_color);
        }
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  // POST CRUD
  const handleSavePost = async () => {
    try {
      const url = editingPost ? `${API}/admin/posts/${editingPost.post_id}` : `${API}/admin/posts`;
      const method = editingPost ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...postForm,
          tags: postForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        })
      });
      
      if (response.ok) {
        toast.success(editingPost ? 'Article updated!' : 'Article created!');
        setPostDialogOpen(false);
        setEditingPost(null);
        setPostForm({ title: '', excerpt: '', content: '', tags: '', read_time: '5 min', cover_image: '', visibility: 'public' });
        fetchData();
      } else {
        toast.error('Failed to save article');
      }
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      tags: post.tags?.join(', ') || '',
      read_time: post.read_time || '5 min',
      cover_image: post.cover_image || '',
      visibility: post.visibility || 'public'
    });
    setPostDialogOpen(true);
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Delete this article?')) return;
    try {
      const response = await fetch(`${API}/admin/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Article deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // JOURNAL CRUD
  const handleSaveJournal = async () => {
    try {
      const url = editingJournal ? `${API}/admin/journals/${editingJournal.journal_id}` : `${API}/admin/journals`;
      const method = editingJournal ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...journalForm,
          tags: journalForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        })
      });
      
      if (response.ok) {
        toast.success(editingJournal ? 'Entry updated!' : 'Entry added!');
        setJournalDialogOpen(false);
        setEditingJournal(null);
        setJournalForm({ content: '', tags: '', image_url: '', visibility: 'public' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  const handleEditJournal = (entry) => {
    setEditingJournal(entry);
    setJournalForm({
      content: entry.content,
      tags: entry.tags?.join(', ') || '',
      image_url: entry.image_url || '',
      visibility: entry.visibility || 'public'
    });
    setJournalDialogOpen(true);
  };

  const handleDeleteJournal = async (journalId) => {
    if (!confirm('Delete this entry?')) return;
    try {
      await fetch(`${API}/admin/journals/${journalId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // PROJECT CRUD
  const handleSaveProject = async () => {
    try {
      const url = editingProject ? `${API}/admin/projects/${editingProject.project_id}` : `${API}/admin/projects`;
      const method = editingProject ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...projectForm,
          tech_stack: projectForm.tech_stack.split(',').map(t => t.trim()).filter(Boolean)
        })
      });
      
      if (response.ok) {
        toast.success(editingProject ? 'Project updated!' : 'Project added!');
        setProjectDialogOpen(false);
        setEditingProject(null);
        setProjectForm({ title: '', description: '', tech_stack: '', github_url: '', demo_url: '', image_url: '', is_featured: false, visibility: 'public' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      title: project.title,
      description: project.description,
      tech_stack: project.tech_stack?.join(', ') || '',
      github_url: project.github_url || '',
      demo_url: project.demo_url || '',
      image_url: project.image_url || '',
      is_featured: project.is_featured || false,
      visibility: project.visibility || 'public'
    });
    setProjectDialogOpen(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm('Delete this project?')) return;
    try {
      await fetch(`${API}/admin/projects/${projectId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // CERTIFICATE CRUD
  const handleSaveCert = async () => {
    try {
      const url = editingCert ? `${API}/admin/certificates/${editingCert.cert_id}` : `${API}/admin/certificates`;
      const method = editingCert ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(certForm)
      });
      
      if (response.ok) {
        toast.success(editingCert ? 'Certificate updated!' : 'Certificate added!');
        setCertDialogOpen(false);
        setEditingCert(null);
        setCertForm({ title: '', issuer: '', date: '', credential_url: '', image_url: '', visibility: 'public' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save certificate');
    }
  };

  const handleEditCert = (cert) => {
    setEditingCert(cert);
    setCertForm({
      title: cert.title,
      issuer: cert.issuer,
      date: cert.date,
      credential_url: cert.credential_url || '',
      image_url: cert.image_url || '',
      visibility: cert.visibility || 'public'
    });
    setCertDialogOpen(true);
  };

  const handleDeleteCert = async (certId) => {
    if (!confirm('Delete this certificate?')) return;
    try {
      await fetch(`${API}/admin/certificates/${certId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Certificate deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // BOOK CRUD
  const handleSaveBook = async () => {
    try {
      const url = editingBook ? `${API}/admin/books/${editingBook.book_id}` : `${API}/admin/books`;
      const method = editingBook ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...bookForm,
          rating: bookForm.rating ? parseInt(bookForm.rating) : null
        })
      });
      
      if (response.ok) {
        toast.success(editingBook ? 'Book updated!' : 'Book added!');
        setBookDialogOpen(false);
        setEditingBook(null);
        setBookForm({ title: '', author: '', status: 'want_to_read', rating: null, notes: '', cover_url: '', visibility: 'public' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save book');
    }
  };

  const handleEditBook = (book) => {
    setEditingBook(book);
    setBookForm({
      title: book.title,
      author: book.author,
      status: book.status || 'want_to_read',
      rating: book.rating || null,
      notes: book.notes || '',
      cover_url: book.cover_url || '',
      visibility: book.visibility || 'public'
    });
    setBookDialogOpen(true);
  };

  const handleDeleteBook = async (bookId) => {
    if (!confirm('Delete this book?')) return;
    try {
      await fetch(`${API}/admin/books/${bookId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Book deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Reset form when dialog closes
  const handlePostDialogChange = (open) => {
    setPostDialogOpen(open);
    if (!open) {
      setEditingPost(null);
      setPostForm({ title: '', excerpt: '', content: '', tags: '', read_time: '5 min', cover_image: '', visibility: 'public' });
    }
  };

  const handleJournalDialogChange = (open) => {
    setJournalDialogOpen(open);
    if (!open) {
      setEditingJournal(null);
      setJournalForm({ content: '', tags: '', image_url: '', visibility: 'public' });
    }
  };

  const handleProjectDialogChange = (open) => {
    setProjectDialogOpen(open);
    if (!open) {
      setEditingProject(null);
      setProjectForm({ title: '', description: '', tech_stack: '', github_url: '', demo_url: '', image_url: '', is_featured: false, visibility: 'public' });
    }
  };

  const handleCertDialogChange = (open) => {
    setCertDialogOpen(open);
    if (!open) {
      setEditingCert(null);
      setCertForm({ title: '', issuer: '', date: '', credential_url: '', image_url: '', visibility: 'public' });
    }
  };

  const handleBookDialogChange = (open) => {
    setBookDialogOpen(open);
    if (!open) {
      setEditingBook(null);
      setBookForm({ title: '', author: '', status: 'want_to_read', rating: null, notes: '', cover_url: '', visibility: 'public' });
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3 h-3 ${star <= (rating || 0) ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'}`}
          />
        ))}
      </div>
    );
  };

  const statusLabels = {
    reading: { label: 'Reading', color: 'bg-blue-500/10 text-blue-500' },
    completed: { label: 'Completed', color: 'bg-green-500/10 text-green-500' },
    want_to_read: { label: 'Want to Read', color: 'bg-yellow-500/10 text-yellow-500' }
  };

  return (
    <div className="min-h-screen py-8" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <p className="mono-label mb-2">Admin Panel</p>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">
            Manage Content
          </h1>
          <p className="text-muted-foreground mt-1">Add articles, journal entries, projects, certificates, and books</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{posts.length}</p>
                  <p className="text-xs text-muted-foreground">Articles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{journals.length}</p>
                  <p className="text-xs text-muted-foreground">Journal</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{projects.length}</p>
                  <p className="text-xs text-muted-foreground">Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{certificates.length}</p>
                  <p className="text-xs text-muted-foreground">Certificates</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <BookMarked className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{books.length}</p>
                  <p className="text-xs text-muted-foreground">Books</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="settings"><Settings className="w-4 h-4 mr-2" />Settings</TabsTrigger>
            <TabsTrigger value="posts"><FileText className="w-4 h-4 mr-2" />Articles</TabsTrigger>
            <TabsTrigger value="journals"><BookOpen className="w-4 h-4 mr-2" />Journal</TabsTrigger>
            <TabsTrigger value="projects"><Briefcase className="w-4 h-4 mr-2" />Projects</TabsTrigger>
            <TabsTrigger value="certificates"><Award className="w-4 h-4 mr-2" />Certificates</TabsTrigger>
            <TabsTrigger value="books"><BookMarked className="w-4 h-4 mr-2" />Books</TabsTrigger>
          </TabsList>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Site Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-primary">Profile</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Display Name</Label>
                      <Input value={siteSettings.name} onChange={(e) => setSiteSettings({...siteSettings, name: e.target.value})} placeholder="geekyrahul" className="mt-1" />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={siteSettings.title} onChange={(e) => setSiteSettings({...siteSettings, title: e.target.value})} placeholder="Senior AI Engineer" className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input value={siteSettings.tagline} onChange={(e) => setSiteSettings({...siteSettings, tagline: e.target.value})} placeholder="We4AI: Let's learn AI together" className="mt-1" />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea value={siteSettings.bio} onChange={(e) => setSiteSettings({...siteSettings, bio: e.target.value})} placeholder="Building intelligent systems..." className="mt-1" rows={3} />
                  </div>
                  <ImageUpload 
                    label="Profile Image"
                    value={siteSettings.profile_image} 
                    onChange={(url) => setSiteSettings({...siteSettings, profile_image: url})} 
                  />
                </div>

                {/* Social Links */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-primary">Social Links</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>GitHub URL</Label>
                      <Input value={siteSettings.github_url} onChange={(e) => setSiteSettings({...siteSettings, github_url: e.target.value})} placeholder="https://github.com/..." className="mt-1" />
                    </div>
                    <div>
                      <Label>LinkedIn URL</Label>
                      <Input value={siteSettings.linkedin_url} onChange={(e) => setSiteSettings({...siteSettings, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/..." className="mt-1" />
                    </div>
                    <div>
                      <Label>Twitter/X URL</Label>
                      <Input value={siteSettings.twitter_url} onChange={(e) => setSiteSettings({...siteSettings, twitter_url: e.target.value})} placeholder="https://twitter.com/..." className="mt-1" />
                    </div>
                    <div>
                      <Label>YouTube URL</Label>
                      <Input value={siteSettings.youtube_url} onChange={(e) => setSiteSettings({...siteSettings, youtube_url: e.target.value})} placeholder="https://youtube.com/@..." className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={siteSettings.email} onChange={(e) => setSiteSettings({...siteSettings, email: e.target.value})} placeholder="hello@we4ai.com" className="mt-1" />
                  </div>
                </div>

                {/* Theme */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-primary flex items-center gap-2"><Palette className="w-4 h-4" /> Theme</h3>
                  <div>
                    <Label>Primary Color (HSL format)</Label>
                    <Input value={siteSettings.primary_color} onChange={(e) => setSiteSettings({...siteSettings, primary_color: e.target.value})} placeholder="160 84% 39%" className="mt-1 font-mono" />
                    <p className="text-xs text-muted-foreground mt-1">Format: H S% L% (e.g., "160 84% 39%" for green, "220 90% 56%" for blue)</p>
                  </div>
                </div>

                {/* Stats & Skills */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Stats (format: value:label, comma separated)</Label>
                    <Input value={statsInput} onChange={(e) => setStatsInput(e.target.value)} placeholder="15+:AI Models, 200+:Papers Read" />
                  </div>
                  <div className="space-y-2">
                    <Label>Skills (comma separated)</Label>
                    <Input value={skillsInput} onChange={(e) => setSkillsInput(e.target.value)} placeholder="LLMs, PyTorch, Python" />
                  </div>
                </div>

                <Button onClick={handleSaveSettings} className="w-full mt-4" data-testid="save-settings-btn">
                  <Save className="w-4 h-4 mr-2" />Save Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* POSTS TAB */}
          <TabsContent value="posts">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Articles</CardTitle>
                <Dialog open={postDialogOpen} onOpenChange={handlePostDialogChange}>
                  <DialogTrigger asChild>
                    <Button className="rounded-lg" data-testid="add-post-btn">
                      <Plus className="w-4 h-4 mr-2" />New Article
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingPost ? 'Edit Article' : 'New Article'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Title</Label>
                        <Input value={postForm.title} onChange={(e) => setPostForm({...postForm, title: e.target.value})} placeholder="Article title" className="mt-1" data-testid="post-title-input" />
                      </div>
                      <div>
                        <Label>Excerpt</Label>
                        <Textarea value={postForm.excerpt} onChange={(e) => setPostForm({...postForm, excerpt: e.target.value})} placeholder="Brief description..." className="mt-1" rows={2} />
                      </div>
                      <ImageUpload 
                        label="Cover Image"
                        value={postForm.cover_image} 
                        onChange={(url) => setPostForm({...postForm, cover_image: url})} 
                      />
                      <div>
                        <Label>Content (Markdown supported)</Label>
                        <Textarea value={postForm.content} onChange={(e) => setPostForm({...postForm, content: e.target.value})} placeholder="Write your article..." className="mt-1 font-mono text-sm" rows={10} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Tags (comma separated)</Label>
                          <Input value={postForm.tags} onChange={(e) => setPostForm({...postForm, tags: e.target.value})} placeholder="AI, LLMs, Tutorial" className="mt-1" />
                        </div>
                        <div>
                          <Label>Read Time</Label>
                          <Input value={postForm.read_time} onChange={(e) => setPostForm({...postForm, read_time: e.target.value})} placeholder="5 min" className="mt-1" />
                        </div>
                      </div>
                      <VisibilitySelector 
                        value={postForm.visibility} 
                        onChange={(v) => setPostForm({...postForm, visibility: v})} 
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                      <Button onClick={handleSavePost} data-testid="save-post-btn"><Save className="w-4 h-4 mr-2" />Save Article</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {posts.map((post) => (
                      <div key={post.post_id} className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium truncate">{post.title}</p>
                            <VisibilityBadge visibility={post.visibility || 'public'} />
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{post.excerpt}</p>
                          <div className="flex gap-2 mt-2">
                            {post.tags?.slice(0, 3).map((tag) => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => handleEditPost(post)} data-testid={`edit-post-${post.post_id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeletePost(post.post_id)} className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {posts.length === 0 && <p className="text-center text-muted-foreground py-8">No articles yet</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* JOURNALS TAB */}
          <TabsContent value="journals">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Journal Entries</CardTitle>
                <Dialog open={journalDialogOpen} onOpenChange={handleJournalDialogChange}>
                  <DialogTrigger asChild>
                    <Button className="rounded-lg" data-testid="add-journal-btn">
                      <Plus className="w-4 h-4 mr-2" />New Entry
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingJournal ? 'Edit Entry' : 'New Journal Entry'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>What did you learn today?</Label>
                        <Textarea value={journalForm.content} onChange={(e) => setJournalForm({...journalForm, content: e.target.value})} placeholder="Today I learned..." className="mt-1" rows={6} data-testid="journal-content-input" />
                      </div>
                      <ImageUpload 
                        label="Attach Image (optional)"
                        value={journalForm.image_url} 
                        onChange={(url) => setJournalForm({...journalForm, image_url: url})} 
                      />
                      <div>
                        <Label>Tags (comma separated)</Label>
                        <Input value={journalForm.tags} onChange={(e) => setJournalForm({...journalForm, tags: e.target.value})} placeholder="LLMs, Reading, Experiment" className="mt-1" />
                      </div>
                      <VisibilitySelector 
                        value={journalForm.visibility} 
                        onChange={(v) => setJournalForm({...journalForm, visibility: v})} 
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                      <Button onClick={handleSaveJournal} data-testid="save-journal-btn"><Save className="w-4 h-4 mr-2" />Save Entry</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {journals.map((entry) => (
                      <div key={entry.journal_id} className="flex items-start justify-between p-4 bg-secondary/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm text-muted-foreground">{new Date(entry.created_at).toLocaleDateString()}</p>
                            <VisibilityBadge visibility={entry.visibility || 'public'} />
                          </div>
                          <p className="line-clamp-2">{entry.content}</p>
                          {entry.image_url && (
                            <img src={entry.image_url} alt="" className="w-16 h-16 object-cover rounded mt-2" />
                          )}
                          <div className="flex gap-2 mt-2">
                            {entry.tags?.map((tag) => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => handleEditJournal(entry)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteJournal(entry.journal_id)} className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {journals.length === 0 && <p className="text-center text-muted-foreground py-8">No entries yet</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROJECTS TAB */}
          <TabsContent value="projects">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Projects</CardTitle>
                <Dialog open={projectDialogOpen} onOpenChange={handleProjectDialogChange}>
                  <DialogTrigger asChild>
                    <Button className="rounded-lg" data-testid="add-project-btn">
                      <Plus className="w-4 h-4 mr-2" />New Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingProject ? 'Edit Project' : 'New Project'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Project Name</Label>
                        <Input value={projectForm.title} onChange={(e) => setProjectForm({...projectForm, title: e.target.value})} placeholder="My AI Project" className="mt-1" data-testid="project-title-input" />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={projectForm.description} onChange={(e) => setProjectForm({...projectForm, description: e.target.value})} placeholder="What does this project do?" className="mt-1" rows={3} />
                      </div>
                      <ImageUpload 
                        label="Project Image"
                        value={projectForm.image_url} 
                        onChange={(url) => setProjectForm({...projectForm, image_url: url})} 
                      />
                      <div>
                        <Label>Tech Stack (comma separated)</Label>
                        <Input value={projectForm.tech_stack} onChange={(e) => setProjectForm({...projectForm, tech_stack: e.target.value})} placeholder="Python, PyTorch, FastAPI" className="mt-1" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>GitHub URL</Label>
                          <Input value={projectForm.github_url} onChange={(e) => setProjectForm({...projectForm, github_url: e.target.value})} placeholder="https://github.com/..." className="mt-1" />
                        </div>
                        <div>
                          <Label>Demo URL</Label>
                          <Input value={projectForm.demo_url} onChange={(e) => setProjectForm({...projectForm, demo_url: e.target.value})} placeholder="https://..." className="mt-1" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={projectForm.is_featured} onCheckedChange={(v) => setProjectForm({...projectForm, is_featured: v})} />
                        <Label>Featured Project</Label>
                      </div>
                      <VisibilitySelector 
                        value={projectForm.visibility} 
                        onChange={(v) => setProjectForm({...projectForm, visibility: v})} 
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                      <Button onClick={handleSaveProject} data-testid="save-project-btn"><Save className="w-4 h-4 mr-2" />Save Project</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {projects.map((project) => (
                      <div key={project.project_id} className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium truncate">{project.title}</p>
                            {project.is_featured && <Badge className="text-xs">Featured</Badge>}
                            <VisibilityBadge visibility={project.visibility || 'public'} />
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{project.description}</p>
                          <div className="flex gap-2 mt-2">
                            {project.tech_stack?.slice(0, 4).map((tech) => <Badge key={tech} variant="outline" className="text-xs font-mono">{tech}</Badge>)}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => handleEditProject(project)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteProject(project.project_id)} className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {projects.length === 0 && <p className="text-center text-muted-foreground py-8">No projects yet</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CERTIFICATES TAB */}
          <TabsContent value="certificates">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Certificates</CardTitle>
                <Dialog open={certDialogOpen} onOpenChange={handleCertDialogChange}>
                  <DialogTrigger asChild>
                    <Button className="rounded-lg" data-testid="add-cert-btn">
                      <Plus className="w-4 h-4 mr-2" />New Certificate
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingCert ? 'Edit Certificate' : 'New Certificate'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Certificate Title</Label>
                        <Input value={certForm.title} onChange={(e) => setCertForm({...certForm, title: e.target.value})} placeholder="AWS Solutions Architect" className="mt-1" data-testid="cert-title-input" />
                      </div>
                      <div>
                        <Label>Issuer</Label>
                        <Input value={certForm.issuer} onChange={(e) => setCertForm({...certForm, issuer: e.target.value})} placeholder="Amazon Web Services" className="mt-1" />
                      </div>
                      <div>
                        <Label>Date</Label>
                        <Input value={certForm.date} onChange={(e) => setCertForm({...certForm, date: e.target.value})} placeholder="January 2024" className="mt-1" />
                      </div>
                      <ImageUpload 
                        label="Certificate Image"
                        value={certForm.image_url} 
                        onChange={(url) => setCertForm({...certForm, image_url: url})} 
                      />
                      <div>
                        <Label>Credential URL (optional)</Label>
                        <Input value={certForm.credential_url} onChange={(e) => setCertForm({...certForm, credential_url: e.target.value})} placeholder="https://..." className="mt-1" />
                      </div>
                      <VisibilitySelector 
                        value={certForm.visibility} 
                        onChange={(v) => setCertForm({...certForm, visibility: v})} 
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                      <Button onClick={handleSaveCert} data-testid="save-cert-btn"><Save className="w-4 h-4 mr-2" />Save Certificate</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {certificates.map((cert) => (
                      <div key={cert.cert_id} className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                            <Award className="w-5 h-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium truncate">{cert.title}</p>
                              <VisibilityBadge visibility={cert.visibility || 'public'} />
                            </div>
                            <p className="text-sm text-muted-foreground">{cert.issuer} · {cert.date}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => handleEditCert(cert)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteCert(cert.cert_id)} className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {certificates.length === 0 && <p className="text-center text-muted-foreground py-8">No certificates yet</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* BOOKS TAB */}
          <TabsContent value="books">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Reading List</CardTitle>
                <Dialog open={bookDialogOpen} onOpenChange={handleBookDialogChange}>
                  <DialogTrigger asChild>
                    <Button className="rounded-lg" data-testid="add-book-btn">
                      <Plus className="w-4 h-4 mr-2" />New Book
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingBook ? 'Edit Book' : 'Add Book'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Book Title</Label>
                        <Input value={bookForm.title} onChange={(e) => setBookForm({...bookForm, title: e.target.value})} placeholder="Attention Is All You Need" className="mt-1" data-testid="book-title-input" />
                      </div>
                      <div>
                        <Label>Author</Label>
                        <Input value={bookForm.author} onChange={(e) => setBookForm({...bookForm, author: e.target.value})} placeholder="Ashish Vaswani et al." className="mt-1" />
                      </div>
                      <ImageUpload 
                        label="Book Cover"
                        value={bookForm.cover_url} 
                        onChange={(url) => setBookForm({...bookForm, cover_url: url})} 
                      />
                      <div>
                        <Label>Status</Label>
                        <Select value={bookForm.status} onValueChange={(v) => setBookForm({...bookForm, status: v})}>
                          <SelectTrigger className="mt-1" data-testid="book-status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="want_to_read">Want to Read</SelectItem>
                            <SelectItem value="reading">Currently Reading</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Rating (1-5 stars)</Label>
                        <Select value={bookForm.rating?.toString() || ''} onValueChange={(v) => setBookForm({...bookForm, rating: v ? parseInt(v) : null})}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="No rating" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">No rating</SelectItem>
                            <SelectItem value="1">1 Star</SelectItem>
                            <SelectItem value="2">2 Stars</SelectItem>
                            <SelectItem value="3">3 Stars</SelectItem>
                            <SelectItem value="4">4 Stars</SelectItem>
                            <SelectItem value="5">5 Stars</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Notes (optional)</Label>
                        <Textarea value={bookForm.notes} onChange={(e) => setBookForm({...bookForm, notes: e.target.value})} placeholder="What did you learn from this book?" className="mt-1" rows={3} />
                      </div>
                      <VisibilitySelector 
                        value={bookForm.visibility} 
                        onChange={(v) => setBookForm({...bookForm, visibility: v})} 
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                      <Button onClick={handleSaveBook} data-testid="save-book-btn"><Save className="w-4 h-4 mr-2" />Save Book</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {books.map((book) => (
                      <div key={book.book_id} className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {book.cover_url ? (
                            <img src={book.cover_url} alt={book.title} className="w-12 h-16 object-cover rounded shrink-0" />
                          ) : (
                            <div className="w-12 h-16 bg-primary/10 rounded flex items-center justify-center shrink-0">
                              <BookMarked className="w-6 h-6 text-primary" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-medium truncate">{book.title}</p>
                              <Badge className={`text-xs ${statusLabels[book.status]?.color}`}>
                                {statusLabels[book.status]?.label}
                              </Badge>
                              <VisibilityBadge visibility={book.visibility || 'public'} />
                            </div>
                            <p className="text-sm text-muted-foreground">by {book.author}</p>
                            {book.rating && <div className="mt-1">{renderStars(book.rating)}</div>}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => handleEditBook(book)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteBook(book.book_id)} className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {books.length === 0 && <p className="text-center text-muted-foreground py-8">No books yet. Start building your reading list!</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
